import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dices, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { generateTournament } from '@/lib/api';
import { ApiError } from '@/lib/api/core';
import { showErrorToast } from '@/lib/toast';

// Story 102 — public, unauthenticated FIDE random tournament generator
// (endorsement checklist service #4). Wraps the endorsed engine's -g mode.
export function FideTournamentGeneratorPage() {
  const { t } = useTranslation('tournament');
  const [players, setPlayers] = useState('20');
  const [rounds, setRounds] = useState('7');
  const [seed, setSeed] = useState('');
  const [generating, setGenerating] = useState(false);
  const [lastSeed, setLastSeed] = useState<number | null>(null);

  async function onGenerate() {
    setGenerating(true);
    try {
      const res = await generateTournament({
        ...(players ? { players: Number(players) } : {}),
        ...(rounds ? { rounds: Number(rounds) } : {}),
        ...(seed ? { seed: Number(seed) } : {}),
      });
      setLastSeed(res.seed);
      const url = URL.createObjectURL(
        new Blob([res.trf], { type: 'text/plain' }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `random-tournament-${res.seed}.trf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('fide_rtg.failed'),
      );
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div data-testid="fide-tournament-generator-page" className="min-h-screen bg-background">
      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="mb-1 text-2xl font-semibold">{t('fide_rtg.title')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{t('fide_rtg.subtitle')}</p>

        <Card>
          <CardContent className="space-y-4 py-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="rtgPlayers">{t('fide_rtg.players_label')}</Label>
                <Input
                  id="rtgPlayers"
                  data-testid="rtg-players-input"
                  type="number"
                  value={players}
                  onChange={(e) => setPlayers(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rtgRounds">{t('fide_rtg.rounds_label')}</Label>
                <Input
                  id="rtgRounds"
                  data-testid="rtg-rounds-input"
                  type="number"
                  value={rounds}
                  onChange={(e) => setRounds(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rtgSeed">{t('fide_rtg.seed_label')}</Label>
                <Input
                  id="rtgSeed"
                  data-testid="rtg-seed-input"
                  type="number"
                  placeholder={t('fide_rtg.seed_placeholder')}
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                />
              </div>
            </div>

            <Button
              data-testid="rtg-generate-button"
              disabled={generating}
              onClick={onGenerate}
            >
              <Dices className="mr-2 h-4 w-4" />
              {generating ? t('fide_rtg.generating') : t('fide_rtg.generate_button')}
            </Button>

            {lastSeed != null && (
              <p data-testid="rtg-last-seed" className="text-sm text-muted-foreground">
                <FileDown className="mr-1 inline h-4 w-4" />
                {t('fide_rtg.downloaded_with_seed', { seed: lastSeed })}{' '}
                <Link
                  to="/fide/pairings-checker"
                  className="text-primary underline underline-offset-2"
                >
                  {t('fide_rtg.check_link')}
                </Link>
              </p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
